import React, { useEffect, useMemo } from "react";
import { SpinnerCitoPlus } from "../loadingLogo";
import {
  useZonaSocialOptions,
  ZONAS_SOCIALES_ENDPOINT,
} from "./useZonaSocialOptions";

const DEFAULT_LABEL = "Zona social";
const DEFAULT_PLACEHOLDER = "Selecciona una zona";
const stringify = (value) =>
  value === null || value === undefined ? "" : String(value);

const ZonaSocialSelect = React.forwardRef(
  (
    {
      value = "",
      onChange,
      name = "zona_uuid",
      id,
      label = DEFAULT_LABEL,
      showLabel = true,
      placeholder = DEFAULT_PLACEHOLDER,
      loadingPlaceholder = "Cargando zonas sociales...",
      allowEmptyOption = true,
      allowAllOption = false,
      allOptionValue = "all",
      allOptionLabel = "Todas las zonas",
      options,
      autoFetch = true,
      endpoint = ZONAS_SOCIALES_ENDPOINT,
      params,
      disabled = false,
      className = "form-select",
      containerClassName,
      labelClassName = "form-label fw-semibold",
      helperText,
      showLoadingSpinner = true,
      spinnerText = "Cargando zonas sociales...",
      onOptionsLoaded,
      onLoadingChange,
      selectedLabel = undefined,
      includeCurrentValueFallback = true,
      loading,
    },
    ref,
  ) => {
    const { zones, isLoading } = useZonaSocialOptions({
      endpoint,
      params,
      autoFetch: options ? false : autoFetch,
      initialOptions: options,
    });

    const effectiveLoading = typeof loading === "boolean" ? loading : isLoading;

    useEffect(() => {
      onLoadingChange?.(effectiveLoading);
    }, [effectiveLoading, onLoadingChange]);

    useEffect(() => {
      if (onOptionsLoaded) {
        onOptionsLoaded(zones);
      }
    }, [zones, onOptionsLoaded]);

    const normalizedOptions = useMemo(() => {
      const base = Array.isArray(zones) ? zones : [];
      const map = new Map();

      base.forEach((option) => {
        if (!option) {
          return;
        }
        const uuid = stringify(option.uuid ?? option.value ?? option.id);
        const nombre = stringify(
          option.nombre ?? option.label ?? option.name ?? "",
        );
        if (!uuid || !nombre) {
          return;
        }
        map.set(uuid, { ...option, uuid, nombre });
      });

      if (allowAllOption) {
        map.set(stringify(allOptionValue), {
          uuid: stringify(allOptionValue),
          nombre: stringify(allOptionLabel),
        });
      }

      if (
        includeCurrentValueFallback &&
        value &&
        !map.has(stringify(value)) &&
        selectedLabel
      ) {
        map.set(stringify(value), {
          uuid: stringify(value),
          nombre: stringify(selectedLabel),
        });
      }

      return Array.from(map.values());
    }, [
      zones,
      allowAllOption,
      allOptionValue,
      allOptionLabel,
      includeCurrentValueFallback,
      value,
      selectedLabel,
    ]);

    const resolvedId = id ?? name ?? "zona-social-select";
    const disableSelect =
      disabled || (effectiveLoading && normalizedOptions.length === 0);

    const resolvedValue = stringify(value);

    const handleChange = (event) => {
      const newValue = stringify(event.target.value);
      const selectedOption = normalizedOptions.find(
        (option) => stringify(option.uuid) === newValue,
      );
      onChange?.(newValue, selectedOption, event);
    };

    const placeholderLabel =
      effectiveLoading && normalizedOptions.length === 0
        ? loadingPlaceholder
        : placeholder;

    return (
      <div className={containerClassName}>
        {showLabel && (
          <label className={labelClassName} htmlFor={resolvedId}>
            {label}
          </label>
        )}

        <select
          ref={ref}
          id={resolvedId}
          name={name}
          className={className}
          value={resolvedValue}
          onChange={handleChange}
          disabled={disableSelect}
        >
          {allowEmptyOption && <option value="">{placeholderLabel}</option>}
          {normalizedOptions.map((option) => (
            <option key={option.uuid} value={option.uuid}>
              {option.nombre}
            </option>
          ))}
        </select>

        {helperText ? <div className="form-text">{helperText}</div> : null}

        {showLoadingSpinner &&
        effectiveLoading &&
        normalizedOptions.length === 0 ? (
          <div className="mt-3">
            <SpinnerCitoPlus text={spinnerText} />
          </div>
        ) : null}
      </div>
    );
  },
);

ZonaSocialSelect.displayName = "ZonaSocialSelect";

export default ZonaSocialSelect;
